/* eslint-disable @typescript-eslint/member-ordering */
import { NzosCMSService } from '##/services/elevate/providers/cms/nzos';
import { ControlConfigService } from '##/services/elevate/providers/config/control';
import { NzosOVPService } from '##/services/elevate/providers/ovp/nzos';
import { NzosIDPService } from '##/services/elevate/providers/idp/nzos';
import { NzosRentalService } from '##/services/elevate/providers/subscriptions/nzos';
import { NzosPlaybackService } from '##/services/elevate/providers/playback/nzos';
import { ServiceTypes } from './types';
import type {
  ConfigService,
  CmsService,
  ServiceRegistry,
  ServiceTypesKeys,
  OVPService,
  IDPService,
  SubscriptionService,
  PlaybackService,
} from './types';

/** Apps should change this as needed. */
const defaultServiceConfig: ServiceRegistry = {
  config: { provider: ControlConfigService.getInstance() },
  cms: { provider: NzosCMSService.getInstance() },
  ovp: { provider: NzosOVPService.getInstance() },
  idp: { provider: NzosIDPService.getInstance() },
  subscriptions: { provider: NzosRentalService.getInstance() },
  playback: { provider: NzosPlaybackService.getInstance() },
};

export class ServiceManager {
  private services = {} as ServiceRegistry;
  // eslint-disable-next-line no-use-before-define
  private static instance?: ServiceManager;

  private constructor(serviceConfig: ServiceRegistry) {
    const serviceTypes = Object.keys(ServiceTypes) as ServiceTypesKeys[];
    serviceTypes.forEach((serviceType: ServiceTypesKeys) => {
      const serviceConfigValue = serviceConfig[serviceType];
      if (serviceConfigValue) {
        this.registerProvider({
          serviceType,
          providerConfig: serviceConfigValue,
        });
      }
    });
  }

  private registerProvider = <TServiceKey extends ServiceTypesKeys>({
    serviceType,
    providerConfig,
  }: {
    serviceType: TServiceKey;
    providerConfig: ServiceRegistry[TServiceKey];
  }): void => {
    this.services[serviceType] = providerConfig;
  };

  private getService = <T extends ServiceTypesKeys>(serviceType: T): ServiceRegistry[T] => this.services[serviceType];

  /** Configuration Service. */
  static getConfigService = (): ConfigService => {
    if (!ServiceManager.instance) {
      ServiceManager.instance = new ServiceManager(defaultServiceConfig);
    }
    return ServiceManager.instance.getService('config').provider;
  };

  /** Content Management Service (CMS). */
  static getCmsService = (): CmsService => {
    if (!ServiceManager.instance) {
      ServiceManager.instance = new ServiceManager(defaultServiceConfig);
    }
    return ServiceManager.instance.getService('cms').provider;
  };

  /** Online Video Provider (OVP) Service. */
  static getOvpService = (): OVPService => {
    if (!ServiceManager.instance) {
      ServiceManager.instance = new ServiceManager(defaultServiceConfig);
    }
    return ServiceManager.instance.getService('ovp').provider;
  };

  /** Identity Provider Service. */
  static getIDPService = (): IDPService | undefined => {
    if (!ServiceManager.instance) {
      ServiceManager.instance = new ServiceManager(defaultServiceConfig);
    }
    return ServiceManager.instance.getService('idp')?.provider;
  };

  static getSubscriptionService = (): SubscriptionService | undefined => {
    if (!ServiceManager.instance) {
      ServiceManager.instance = new ServiceManager(defaultServiceConfig);
    }
    return ServiceManager.instance.getService('subscriptions')?.provider;
  };

  static getPlaybackService = (): PlaybackService | undefined => {
    if (!ServiceManager.instance) {
      ServiceManager.instance = new ServiceManager(defaultServiceConfig);
    }

    return ServiceManager.instance.getService('playback')?.provider;
  };
}
